<template>
  <div class="parser-manager">
    <!-- 顶部操作栏 -->
    <div class="top-bar">
      <div class="left">
        <div class="search-input">
          <Input
            v-model="searchText"
            placeholder="搜索提示词名称或内容..."
            icon="md-search"
            clearable
            @on-change="handleSearch"
          />
        </div>
        <div class="filter-type-tags">
          <Tag
            :color="filterType === '' ? 'primary' : 'default'"
            class="filter-tag"
            @click.native="filterType = ''"
          >
            全部
          </Tag>
          <Tag
            :color="filterType === 'my' ? 'primary' : 'default'"
            class="filter-tag"
            @click.native="filterType = 'my'"
          >
            我的
          </Tag>
          <Tag
            :color="filterType === 'shared' ? 'primary' : 'default'"
            class="filter-tag"
            @click.native="filterType = 'shared'"
          >
            共享
          </Tag>
        </div>
        <div class="filter-mod-wrap">
          <Select
            v-model="filterMod"
            filterable
            clearable
            placeholder="模块"
            style="width: 200px"
            transfer
          >
            <Option value="">所有</Option>
            <Option
              v-for="m in modFilterOptions"
              :key="m"
              :value="m"
              :label="modOptionLabel(m)"
            >
              {{ modOptionLabel(m) }}
            </Option>
          </Select>
        </div>
      </div>
      
      <!-- 部门筛选标签 -->
        <div class="filter-tags">
          <Tag 
            :color="filterDep === '' ? 'primary' : 'default'"
            @click.native="filterByDepartment('')"
            class="filter-tag"
          >
            全部 ({{ totalCount }})
          </Tag>
          <Tag 
            :color="filterDep === 'null' ? 'primary' : 'default'"
            @click.native="filterByDepartment('null')"
            class="filter-tag"
          >
            通用 ({{ nullDepCount }})
          </Tag>
          <Tag 
            v-for="dep in departmentStats" 
            :key="dep.id"
            :color="filterDep === dep.id ? 'primary' : 'default'"
            @click.native="filterByDepartment(dep.id)"
            class="filter-tag"
          >
            {{ dep.name }} ({{ dep.count }})
          </Tag>
        </div>


      <div class="right">
        <Button type="primary" icon="md-add" @click="showCreateModal = true">
          新建提示词
        </Button>
      </div>
    </div>

    <!-- 提示词列表 -->
    <div class="parser-list">
      <div v-if="loading" class="loading-container">
        <Spin size="large" />
        <p>加载中...</p>
      </div>

      <div v-else-if="filteredItems.length === 0" class="empty-container">
        <Icon type="md-document" size="64" color="#ccc" />
        <p>暂无提示词</p>
        <Button type="primary" @click="showCreateModal = true">创建第一个提示词</Button>
      </div>

      <div v-else class="items-grid">
        <div
          v-for="item in filteredItems"
          :key="item.id"
          class="parser-item"
          :class="{ 'shared': item.shared }"
          @click="openDetailModal(item)"
        >
          <div class="item-header">
            <div class="item-header-top">
              <div class="item-title">
                <h3>{{ item.name }}</h3>
              </div>
              <div class="item-header-actions" @click.stop>
                <Button
                  type="text"
                  icon="md-copy"
                  size="small"
                  title="复制内容"
                  @click="copyContent(item.content)"
                />
                <Button
                  type="text"
                  icon="md-create"
                  size="small"
                  title="编辑"
                  @click="editItem(item)"
                />
                <Dropdown v-if="canDelete(item)" trigger="click" @on-click="handleAction($event, item)">
                  <Button type="text" icon="md-more" size="small" title="更多" />
                  <DropdownMenu slot="list">
                    <DropdownItem name="delete">删除</DropdownItem>
                  </DropdownMenu>
                </Dropdown>
              </div>
            </div>
            <div class="item-tags-row">
              <Tag v-if="item.shared" color="green" size="small">共享</Tag>
              <Tag v-if="item.dep_id" color="blue" size="small">{{ getDepartmentName(item.dep_id) }}</Tag>
              <Tag v-if="item.mod" color="orange" size="small">{{ labelForMod(item.mod) }}</Tag>
            </div>
          </div>

          <div class="item-content">
            <p>{{ getContentSummary(item.content) }}</p>
            <div v-if="item.demo" class="item-demo">
              <p class="demo-label">应用方法：</p>
              <p>{{ getContentSummary(item.demo) }}</p>
            </div>
          </div>

          <div class="item-footer">
            <div class="item-meta">
              <BaseNameBoard :size="16" :uid="item.created_by" />
              <span class="created-time">{{ formatTime(item.created_at) }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 创建/编辑模态框 -->
    <Modal
      v-model="showCreateModal"
      :title="editingItem ? '编辑提示词' : '新建提示词'"
      width="1200"
      :mask-closable="false"
    >
      <Form ref="form" :model="formData" :rules="formRules" label-position="top" class="parser-form-body">
        <Row :gutter="20" class="parser-form-row">
          <i-col :span="14" class="parser-form-col-left">
            <FormItem label="提示词名称" prop="name">
              <Input
                v-model="formData.name"
                placeholder="为您的提示词取一个名字"
                maxlength="50"
                show-word-limit
              />
            </FormItem>
            <FormItem label="所属部门" prop="dep_id">
              <BaseDepSelectPro v-model="formData.dep_id" placeholder="可选，不选表示不限定事业部" clearable />
            </FormItem>
            <FormItem label="提示词内容" prop="content" class="parser-form-item-content">
              <Input
                v-model="formData.content"
                type="textarea"
                :rows="18"
                placeholder="请输入提示词内容..."
                class="parser-content-textarea"
              />
            </FormItem>
          </i-col>
          <i-col :span="10" class="parser-form-col-right">
            <FormItem label="模块" prop="mod">
              <Select
                v-model="formData.mod"
                filterable
                clearable
                transfer
                placeholder="选择模块（可选）"
              >
                <Option value="">通用 / 未指定</Option>
                <Option
                  v-for="m in formModSelectOptions"
                  :key="m"
                  :value="m"
                >
                  {{ modOptionLabel(m) }}
                </Option>
              </Select>
            </FormItem>
            <FormItem label="共享设置">
              <Checkbox v-model="formData.shared">
                共享给其他用户
              </Checkbox>
              <p class="form-tip">共享的提示词可以被其他用户查看和使用</p>
            </FormItem>
            <FormItem label="AI应用选择及应用方法介绍" prop="demo">
              <Input
                v-model="formData.demo"
                type="textarea"
                :rows="12"
                placeholder="请介绍该提示词的使用方法和应用场景..."
                class="parser-demo-textarea"
              />
            </FormItem>
          </i-col>
        </Row>
      </Form>

      <div slot="footer" class="parser-form-footer">
        <Button @click="showCreateModal = false">取消</Button>
        <Button
          type="primary"
          :loading="submitting"
          @click="handleSubmit"
        >
          {{ editingItem ? '保存' : '创建' }}
        </Button>
      </div>
    </Modal>

    <!-- 详情展示模态框 -->
    <Modal
      v-model="showDetailModal"
      :title="detailItem ? detailItem.name : '提示词详情'"
      width="800"
      :mask-closable="true"
      footer-hide
    >
      <div v-if="detailItem" class="detail-content">
        <div class="detail-header">
          <div class="detail-title">
            <h2>{{ detailItem.name }}</h2>
            <div class="detail-meta">
              <Tag v-if="detailItem.shared" color="green" size="small">共享</Tag>
              <Tag v-if="detailItem.dep_id" color="blue" size="small">{{ getDepartmentName(detailItem.dep_id) }}</Tag>
              <Tag v-if="detailItem.mod" color="orange" size="small">{{ labelForMod(detailItem.mod) }} · {{ detailItem.mod }}</Tag>
            </div>
          </div>
          <div class="detail-actions">
            <Button type="primary" icon="md-copy" @click="copyContent(detailItem.content)">
              复制提示词
            </Button>
          </div>
        </div>

        <div class="detail-section">
          <h3>应用方法介绍</h3>
          <div v-if="detailItem.demo" class="demo-content">
            <div class="demo-text">{{ detailItem.demo }}</div>
          </div>
          <div v-else class="no-demo">
            <p>暂无应用方法介绍</p>
          </div>
        </div>

        <div class="detail-section">
          <h3>提示词内容</h3>
          <div class="content-preview">
            <pre>{{ detailItem.content }}</pre>
          </div>
        </div>

        <div class="detail-footer">
          <div class="detail-info">
            <span>创建者：<BaseNameBoard :size="16" :uid="detailItem.created_by" /></span>
            <span>创建时间：{{ formatTime(detailItem.created_at) }}</span>
          </div>
        </div>
      </div>
    </Modal>

    <!-- 确认删除模态框 -->
    <Modal v-model="showDeleteModal" title="确认删除" width="400">
      <p>确定要删除提示词 "{{ deletingItem && deletingItem.name }}" 吗？</p>
      <p style="color: #999; font-size: 12px;">此操作不可恢复</p>

      <div slot="footer">
        <Button @click="showDeleteModal = false">取消</Button>
        <Button
          type="error"
          :loading="deleting"
          @click="confirmDelete"
        >
          确认删除
        </Button>
      </div>
    </Modal>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import moment from 'moment';
import {ZZL_MOD_LABELS, labelForMod} from '@/components/ai/aiPromptModLabels';

export default {
  name: 'ParserManager',
  data() {
    return {
      loading: false,
      submitting: false,
      deleting: false,
      items: [],
      searchText: '',
      filterType: '',
      filterMod: '',
      filterDep: '',
      showCreateModal: false,
      showDeleteModal: false,
      showDetailModal: false,
      editingItem: null,
      deletingItem: null,
      detailItem: null,
      formData: {
        name: '',
        content: '',
        dep_id: '',
        demo: '',
        shared: false,
        mod: '',
      },
      formRules: {
        name: [
          {required: true, message: '请输入提示词名称', trigger: 'blur'},
          {min: 1, max: 50, message: '名称长度在 1 到 50 个字符', trigger: 'blur'},
        ],
        content: [
          {required: true, message: '请输入提示词内容', trigger: 'blur'},
          {
            validator: (rule, val, cb) => {
              if (val == null || String(val).trim() === '') {
                cb(new Error('请输入提示词内容'));
              } else {
                cb();
              }
            },
            trigger: 'blur',
          },
        ],
      },
    };
  },
  computed: {
    ...mapGetters('session', ['session']),

    filteredItems() {
      let items = this.items;

      // 搜索过滤
      if (this.searchText) {
        const searchLower = this.searchText.toLowerCase();
        items = items.filter((item) =>
          item.name.toLowerCase().includes(searchLower) ||
          item.content.toLowerCase().includes(searchLower) ||
          (item.demo && item.demo.toLowerCase().includes(searchLower)),
        );
      }

      // 类型过滤
      if (this.filterType === 'my') {
        items = items.filter((item) => item.created_by === this.session.id);
      } else if (this.filterType === 'shared') {
        items = items.filter((item) => item.shared);
      }

      // 模块过滤
      if (this.filterMod) {
        items = items.filter((item) => (item.mod || '') === this.filterMod);
      }

      // 部门过滤
      if (this.filterDep) {
        if (this.filterDep === 'null') {
          items = items.filter((item) => !item.dep_id || item.dep_id === null);
        } else {
          items = items.filter((item) => item.dep_id === this.filterDep);
        }
      }

      return items;
    },

    currentUserId() {
      return this.session && this.session.id;
    },

    isAdmin() {
      return (this.session && this.session.role === 'admin') || (this.session && this.session.is_admin);
    },

    departments() {
      const deps = this.$store.getters['session/deps'];
      return deps ? deps.filter(dep => dep.ignore !== 1) : [];
    },

    // 部门统计
    departmentStats() {
      const deps = this.departments;
      const stats = [];
      
      deps.forEach(dep => {
        const count = this.items.filter(item => item.dep_id === dep.id).length;
        if (count > 0) {
          stats.push({
            id: dep.id,
            name: dep.name,
            count: count
          });
        }
      });
      
      return stats.sort((a, b) => b.count - a.count); // 按数量降序排列
    },

    // 总数统计
    totalCount() {
      return this.items.length;
    },

    // 通用（无部门）统计
    nullDepCount() {
      return this.items.filter(item => !item.dep_id || item.dep_id === null).length;
    },

    /** zzl 各页 AiPromptPanel 的 mod 及日报组合，作录入预设 */
    zzlModPresets() {
      const fromLabels = Object.keys(ZZL_MOD_LABELS);
      const daily = [];
      ['pmis', 'jlis', 'jgis'].forEach((p) => {
        ['daily', 'month', 'season', 'free'].forEach((t) => {
          daily.push(`zzl-daily-${p}-${t}`);
        });
      });
      return [...new Set([...fromLabels, ...daily])].sort();
    },

    /** 筛选下拉里：预设 ∪ 当前列表里出现过的 mod */
    modFilterOptions() {
      const fromItems = this.items.map((i) => i.mod).filter((m) => m && String(m).trim());
      const merged = [...new Set([...this.zzlModPresets, ...fromItems])];
      return merged.sort();
    },

    /** 表单模块下拉：预设 ∪ 已有数据中的 mod，并保证当前编辑值出现在选项中 */
    formModSelectOptions() {
      const merged = [...new Set([...this.zzlModPresets, ...this.items.map((i) => i.mod).filter(Boolean)])];
      const m = this.formData.mod;
      if (m && String(m).trim() && !merged.includes(m)) {
        merged.push(m);
      }
      return merged.sort();
    },
  },

  mounted() {
    this.loadData();
  },

  methods: {
    labelForMod,

    modOptionLabel(m) {
      const lab = labelForMod(m);
      return lab === m ? m : `${lab} · ${m}`;
    },

    // 加载数据
    async loadData() {
      this.loading = true;
      try {
        const res = await this.$api.get('/ai/parse?q=all');
        this.items = res.data.data || [];
      } catch (error) {
        this.Error('加载提示词列表失败');
        console.error('Load parsers error:', error);
      } finally {
        this.loading = false;
      }
    },

    // 搜索处理
    handleSearch() {
      // 实时搜索，不需要额外处理
    },

    // 部门筛选处理
    filterByDepartment(depId) {
      this.filterDep = depId;
    },

    // 显示详情模态框
    openDetailModal(item) {
      this.detailItem = item;
      this.showDetailModal = true;
    },

    // 获取内容摘要
    getContentSummary(content) {
      if (!content) return '';
      const summary = content.replace(/\n/g, ' ').trim();
      return summary.length > 30 ? summary.substring(0, 30) + '...' : summary;
    },

    // 格式化时间
    formatTime(time) {
      if (!time) return '';
      return moment(time).format('YYYY-MM-DD HH:mm');
    },

    // 获取部门名称
    getDepartmentName(depId) {
      if (!depId) return '';
      const deps = this.$store.getters['session/deps'];
      if (!deps) return '';
      const dep = deps.find(d => d.id === depId);
      return dep ? dep.name : '';
    },

    // 检查是否可以删除
    canDelete(item) {
      return this.isAdmin || item.created_by === this.currentUserId;
    },

    // 处理操作（仅删除，编辑已外置为按钮）
    handleAction(action, item) {
      if (action === 'delete') {
        this.deleteItem(item);
      }
    },

    // 编辑项目
    editItem(item) {
      this.editingItem = item;
      this.formData = {
        name: item.name,
        content: item.content,
        dep_id: item.dep_id || '',
        demo: item.demo || '',
        shared: item.shared,
        mod: item.mod || '',
      };
      this.showCreateModal = true;
    },

    // 复制内容
    copyContent(content) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(content).then(() => {
          this.Success('内容已复制到剪贴板');
        }).catch(() => {
          this.fallbackCopy(content);
        });
      } else {
        this.fallbackCopy(content);
      }
    },

    // 备用复制方法
    fallbackCopy(text) {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        this.Success('内容已复制到剪贴板');
      } catch (err) {
        this.Error('复制失败');
      }
      document.body.removeChild(textArea);
    },

    // 删除项目
    deleteItem(item) {
      this.deletingItem = item;
      this.showDeleteModal = true;
    },

    // 确认删除
    async confirmDelete() {
      if (!this.deletingItem) return;

      this.deleting = true;
      try {
        await this.$api.delete(`/ai/parse/${this.deletingItem.id}`);
        this.Success('删除成功');
        this.showDeleteModal = false;
        this.deletingItem = null;
        this.loadData();
      } catch (error) {
        this.Error('删除失败');
        console.error('Delete parser error:', error);
      } finally {
        this.deleting = false;
      }
    },

    // 提交表单
    async handleSubmit() {
      const valid = await this.$refs.form.validate();
      if (!valid) return;

      this.submitting = true;
      try {
        if (this.editingItem) {
          // 更新
          await this.$api.put(`/ai/parse/${this.editingItem.id}`, this.formData);
          this.Success('更新成功');
        } else {
          // 创建
          await this.$api.post('/ai/parse', this.formData);
          this.Success('创建成功');
        }

        this.showCreateModal = false;
        this.resetForm();
        this.loadData();
      } catch (error) {
        this.Error(this.editingItem ? '更新失败' : '创建失败');
        console.error('Submit parser error:', error);
      } finally {
        this.submitting = false;
      }
    },

    // 重置表单
    resetForm() {
      this.editingItem = null;
      this.formData = {
        name: '',
        content: '',
        dep_id: '',
        demo: '',
        shared: false,
        mod: '',
      };
      this.$refs.form.resetFields();
    },
  },
};
</script>

<style lang="less" scoped>
.parser-manager {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  background: var(--bg2);
  overflow: hidden;
  box-sizing: border-box;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  margin-bottom: 16px;

  .left {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    .search-input {
      width: 300px;
    }

    .filter-type-tags {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
      align-items: center;

      .filter-tag {
        cursor: pointer;
        margin: 0;
      }
    }

    .filter-mod-wrap {
      flex-shrink: 0;
    }
  }

  .filter-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;

    .filter-tag {
      cursor: pointer;
      margin: 0;
    }
  }
}

.parser-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.department-filter {
  margin-bottom: 24px;
  padding: 16px;
  background: var(--bg2);
  border-radius: 8px;
  border: 1px solid var(--border);

  .filter-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;

    .filter-tag {
      cursor: pointer;
      transition: all 0.3s ease;
      font-size: 13px;
      padding: 4px 12px;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      }
    }
  }
}

.loading-container,
.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: var(--subtext2);

  p {
    margin: 16px 0 0 0;
    font-size: 16px;
  }
}

.items-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.parser-item {
  background: var(--bg2);
  border: 1px solid var(--border);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    border-color: var(--primary);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  &.shared {
    border-color: #52c41a;
  }
}

.item-header {
  margin-bottom: 12px;

  .item-header-top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
  }

  .item-title {
    flex: 1;
    min-width: 0;
    margin-right: 8px;

    h3 {
      margin: 0;
      font-size: 16px;
      font-weight: 600;
      color: var(--text2);
      line-height: 1.4;
    }
  }

  .item-header-actions {
    display: flex;
    align-items: center;
    flex-shrink: 0;
    gap: 2px;

    .ivu-btn {
      padding: 4px 6px;
      border-radius: 4px;

      &:hover {
        background: var(--bg3);
        color: var(--primary);
      }
    }
  }

  .item-tags-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    margin-top: 8px;
  }
}

.item-content {
  margin-bottom: 16px;

  p {
    margin: 0;
    color: var(--text2);
    font-size: 14px;
    line-height: 1.5;
    word-break: break-word;
  }

  .item-demo {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid var(--border);

    .demo-label {
      font-size: 12px;
      color: var(--subtext2);
      margin-bottom: 4px;
    }

    p {
      font-size: 13px;
      color: var(--text2);
    }
  }
}

.item-footer {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  .item-meta {
    display: flex;
    align-items: center;
    gap: 8px;

    .created-time {
      color: var(--subtext2);
      font-size: 12px;
    }
  }
}

.form-tip {
  margin: 4px 0 0 0;
  color: var(--subtext2);
  font-size: 12px;
}

.parser-form-body ::v-deep .ivu-form-item {
  margin-bottom: 14px;
}

.parser-form-col-left {
  border-right: 1px solid var(--border);
  padding-right: 8px;
}

.parser-form-col-right {
  padding-left: 8px;
}

.parser-form-item-content ::v-deep .ivu-form-item-content {
  line-height: 1.4;
}

.parser-content-textarea ::v-deep textarea {
  resize: none;
  min-height: 380px;
  font-family: inherit;
}

.parser-demo-textarea ::v-deep textarea {
  resize: none;
  min-height: 200px;
}

.parser-form-footer {
  text-align: right;
}

// 响应式设计
@media (max-width: 768px) {
  .parser-manager {
    padding: 16px;
  }

  .top-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;

    .left {
      flex-direction: column;
      width: 100%;

      .search-input {
        width: 100%;
      }
    }
  }

  .department-filter {
    .filter-tags {
      gap: 6px;
      
      .filter-tag {
        font-size: 12px;
        padding: 3px 8px;
      }
    }
  }

  .items-grid {
    grid-template-columns: 1fr;
  }
}

// 详情模态框样式
.detail-content {
  .detail-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--border);

    .detail-title {
      flex: 1;
      margin-right: 16px;

      h2 {
        margin: 0 0 8px 0;
        color: var(--text2);
        font-size: 20px;
        font-weight: 600;
      }

      .detail-meta {
        display: flex;
        gap: 8px;
      }
    }

    .detail-actions {
      flex-shrink: 0;
    }
  }

  .detail-section {
    margin-bottom: 24px;

    h3 {
      margin: 0 0 12px 0;
      color: var(--text2);
      font-size: 16px;
      font-weight: 600;
      padding-bottom: 8px;
      border-bottom: 1px solid var(--border);
    }

    .demo-content {
      background: var(--bg3);
      border-radius: 6px;
      padding: 16px;
      border-left: 4px solid var(--primary);

      .demo-text {
        color: var(--text2);
        font-size: 14px;
        line-height: 1.6;
        white-space: pre-wrap;
        word-break: break-word;
      }
    }

    .no-demo {
      text-align: center;
      padding: 20px;
      color: var(--subtext2);
      background: var(--bg3);
      border-radius: 6px;
      border: 1px dashed var(--border);
    }

    .content-preview {
      background: var(--bg3);
      border-radius: 6px;
      padding: 16px;
      border: 1px solid var(--border);
      max-height: 300px;
      overflow-y: auto;

      pre {
        margin: 0;
        color: var(--text2);
        font-size: 13px;
        line-height: 1.5;
        white-space: pre-wrap;
        word-break: break-word;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
      }
    }
  }

  .detail-footer {
    margin-top: 24px;
    padding-top: 16px;
    border-top: 1px solid var(--border);

    .detail-info {
      display: flex;
      gap: 24px;
      color: var(--subtext2);
      font-size: 13px;

      span {
        display: flex;
        align-items: center;
        gap: 4px;
      }
    }
  }
}
</style>
